<script setup>
import { __, __x } from 'JIX/gettext.js';
import { useStore } from 'vuex';
import { computed, ref, watch, onMounted } from 'vue';
import DsAddressField from 'JIX/components/ds/DsAddressField.vue';
import { v4 as uuidv4 } from 'uuid';
import ValidationError from '../support/components/ValidationError.vue';
import { isMobile } from 'JIX/utils.js';
import debounce from 'lodash/debounce';

const props = defineProps({
    showTitle: {
        type: Boolean,
        default: true,
    },
});
const store = useStore();

const address = defineModel('address', String);
const radius = defineModel('radius', Number);

const addressProxy = ref(address.value);
const radiusProxy = ref(radius.value);

const invalidAddressError = computed(() =>
    store.state.apiValidationErrors ? store.state.apiValidationErrors.find(error => error.field === 'address') : null);

const radiusInputRef = ref(null);

const id = uuidv4();
const distanceToJobId = 'distancetojob--' + id;
const addressId = 'address--' + id;

const MIN_RADIUS = 1;
const MAX_RADIUS = 100;

function hasRadius(radius) {
    return radius || radius === 0;
}

function validateRadius(radius) {
    return typeof radius === 'number' && MIN_RADIUS <= radiusProxy.value && radiusProxy.value <= MAX_RADIUS;
}

const invalidRadiusErrorMessage = computed(() => {
    if (hasRadius(radiusProxy.value) && !validateRadius(radiusProxy.value)) {
        return __x('Radius skal være mellem {min} km og {max} km', { min: MIN_RADIUS, max: MAX_RADIUS });
    }
    return '';
});

watch([addressProxy, radiusProxy], debounce(() => {
    if (hasRadius(radiusProxy.value) && addressProxy.value && !invalidRadiusErrorMessage.value) {
        radius.value = radiusProxy.value;
        address.value = addressProxy.value;
    } else {
        radius.value = "";
        address.value = "";
    }
}, 300));

defineExpose({
    clearLocalValues: () => {
        radiusProxy.value = '';
        addressProxy.value = '';
    },
});

onMounted(() => {
    if (!isMobile.value){
        radiusInputRef.value?.focus();
    }
    if (!radius.value && store.state.userAddress){
        addressProxy.value = store.state.userAddress;
    }
});

</script>

<template>
    <h5 v-if="props.showTitle">
        {{ __('Geografi') }}
    </h5>
    <div>
        <label class="control-label" :for="distanceToJobId" :class="{ 'text-danger': invalidRadiusErrorMessage }">
            {{ __('Hvor langt vil du have til arbejde?') }}
        </label>
        <input
            class="form-control"
            ref="radiusInputRef"
            :id="distanceToJobId"
            type="number"
            :max="MAX_RADIUS"
            :min="MIN_RADIUS"
            v-model="radiusProxy"
            :class="{ 'error-border-color': invalidRadiusErrorMessage }"
        >
        <small class="form-text text-muted">
            {{ __('Antal km') }}
        </small>
        <ValidationError :msg="invalidRadiusErrorMessage" />
    </div>
    <div class="mt-3">
        <label
            class="control-label"
            :for="addressId"
            :class="invalidAddressError ? 'text-danger' : ''"
        >
            {{ __('Fra hvilken adresse?') }}
        </label>
        <DsAddressField
            :id="addressId"
            v-model="addressProxy"
            :class="invalidAddressError ? 'error-border-color' : ''"
        />
        <ValidationError :msg="invalidAddressError ? invalidAddressError.error_message : null" />
        <slot />
    </div>
</template>